import React from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';

import { ArrowLeftIcon, ArrowRightIcon, CloseIcon } from '../../icons';

import { Box } from '../Box';
import { Text } from '../Text';
import { ThemeContainer } from '../ThemeContainer';
import { IconButton } from '../IconButton';
import { Stack } from '../Stack';
import { Heading } from '../Heading';

import { GalleryImageProps } from './_Gallery';

import * as styles from './dialog.css';

type ImageGalleryDialogProps = {
  isOpen: boolean;
  activeImage: number | null;
  gallery: GalleryImageProps[];
  handleOnCloseOutFocus: (event: Event, focusedIndex: number) => void;
  handleOpenChange: (isOpen: boolean) => void;
};

export const ImageGalleryDialog = ({
  isOpen,
  activeImage,
  gallery,
  handleOpenChange,
  handleOnCloseOutFocus,
}: ImageGalleryDialogProps) => {
  const refClose = React.useRef<HTMLButtonElement>(null);
  const [currentIndex, setCurrentIndex] = React.useState<number>(activeImage ?? 0);

  const amountOfImages = gallery.length;

  const goTo = React.useCallback(
    (index: number) => {
      setCurrentIndex(Math.max(0, Math.min(index, amountOfImages - 1)));
    },
    [amountOfImages]
  );

  const setFocusToClose = (setFocus?: boolean) => {
    if (setFocus) {
      refClose.current?.focus();
    }
  };

  const activeImageIndex = React.useMemo(() => {
    return currentIndex !== null ? currentIndex : activeImage ? activeImage : 0;
  }, [activeImage, currentIndex]);

  const currentImageProps = React.useMemo(() => {
    return gallery[currentIndex ?? 0];
  }, [currentIndex, gallery]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') goTo(currentIndex + 1);
      if (event.key === 'ArrowLeft') goTo(currentIndex - 1);
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, amountOfImages, goTo]);

  React.useEffect(() => {
    if (isOpen) setCurrentIndex(activeImage ?? 0);
  }, [isOpen, activeImage]);

  return (
    <RadixDialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <RadixDialog.Portal>
        <ThemeContainer>
          <RadixDialog.Overlay className={styles.overlay} />
          <RadixDialog.Content
            className={styles.dialogContent}
            onCloseAutoFocus={(event) => {
              const imageIndex = activeImageIndex ?? activeImage;

              handleOnCloseOutFocus(event, imageIndex + 1);
            }}
          >
            <RadixDialog.Close
              className={styles.closeButton}
              asChild
              onClick={(event) => event.stopPropagation()}
              ref={refClose}
            >
              <IconButton
                icon={<CloseIcon />}
                label="Sluit"
                tone="onDark"
                shape="brand"
                variant="solid"
              />
            </RadixDialog.Close>
            <Box className={styles.innerContent} width="100%" height="100%" asChild>
              <Stack gap={4}>
                <RadixDialog.Title asChild>
                  <Heading fontWeight="regular" color="onDark" size="h4" as="h2">
                    {currentImageProps?.title}
                  </Heading>
                </RadixDialog.Title>
                <Box bg="backgroundLight" overflow="hidden" borderRadius="brandXl">
                  <Stack>
                    <Box overflow="hidden">
                      {currentImageProps ? currentImageProps.image : null}
                    </Box>
                    {currentImageProps.description ? (
                      <RadixDialog.Description asChild>
                        <Box paddingBlock={4} paddingInline={7}>
                          {currentImageProps.description}
                        </Box>
                      </RadixDialog.Description>
                    ) : null}
                  </Stack>
                </Box>
                <Stack direction="row" gap={6} alignX="center" alignY="center" aria-live="polite">
                  <IconButton
                    size="small"
                    icon={<ArrowLeftIcon size="xs" />}
                    disabled={activeImageIndex === 0}
                    tone="onDark"
                    onClick={() => {
                      const previousIndex = activeImageIndex - 1;
                      goTo(previousIndex);
                      setFocusToClose(previousIndex === 0);
                    }}
                    label="Vorige"
                    aria-label="Vorige"
                  />
                  {amountOfImages > 1 ? (
                    <Text color="onDark" size="paragraph">
                      {currentIndex + 1} / {amountOfImages}
                    </Text>
                  ) : null}
                  <IconButton
                    size="small"
                    disabled={activeImageIndex === amountOfImages - 1}
                    tone="onDark"
                    aria-label="Volgende"
                    onClick={() => {
                      const nextIndex = activeImageIndex + 1;
                      goTo(nextIndex);
                      setFocusToClose(nextIndex === amountOfImages - 1);
                    }}
                    icon={<ArrowRightIcon size="xs" />}
                    label="Volgende"
                  />
                </Stack>
              </Stack>
            </Box>
          </RadixDialog.Content>
        </ThemeContainer>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

